/**
 * Bag behaviour (port of web/src/components/CartProvider.tsx + CartDrawer/CartPage): the real Shopify cart.
 * - Line changes go through /cart/change.js and the note through /cart/update.js, one request at a time.
 * - After every change the drawer (inside the header section) and the bag page are re-rendered from the
 *   server with the Section Rendering API, so totals, discounts and stock limits come from Shopify.
 * - Stock-limit errors (HTTP 422) are shown on the line that caused them.
 * - Adds from anywhere (home card quick-add, Horizon's product form) fire `cart:update`; we refresh and
 *   open the drawer. The header count bubble (Horizon's cart-icon) listens to the same event.
 * - Back/forward cache: the cart is refreshed on `pageshow` so a restored page never shows a stale bag.
 */
import { attachDragDismiss } from '@theme/yushe-drag-dismiss';

const SRC = 'yushe-cart';
const CLOSE_MS = 450;
const routes = (window.Theme && window.Theme.routes) || {};
const CART_URL = routes.cart_url || '/cart';
const CHANGE_URL = routes.cart_change_url || '/cart/change';
const UPDATE_URL = routes.cart_update_url || '/cart/update';

let chain = Promise.resolve();
/** Runs cart requests strictly one after another. */
const serial = (fn) => (chain = chain.then(fn, fn));

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

function roots() {
  return {
    drawer: document.querySelector('yushe-cart-drawer'),
    page: document.querySelector('yushe-cart-page'),
  };
}
function sectionIds() {
  const { drawer, page } = roots();
  return [drawer?.dataset.sectionId, page?.dataset.sectionId].filter(Boolean);
}

/** The multi-buy line rises in only when its message changed (a tier was reached or lost), never on every swap. */
function markTierChange(cur, next) {
  const a = cur.querySelector('[data-cart-tier]');
  const b = next.querySelector('[data-cart-tier]');
  if (b && a?.textContent.trim() !== b.textContent.trim()) b.setAttribute('data-changed', '');
  // The ladder fills from where it was, not from its new width (the swap replaces the element).
  const fa = a?.querySelector('.offer__fill');
  const fb = b?.querySelector('.offer__fill');
  if (fa && fb && fa.style.width !== fb.style.width) {
    const to = fb.style.width;
    fb.style.width = fa.style.width;
    requestAnimationFrame(() => requestAnimationFrame(() => { fb.style.width = to; }));
  }
}

/** Swap the server-rendered parts into the live DOM. */
function apply(sections) {
  const { drawer, page } = roots();
  if (drawer && sections[drawer.dataset.sectionId]) {
    const doc = new DOMParser().parseFromString(sections[drawer.dataset.sectionId], 'text/html');
    const next = doc.querySelector('[data-cart-root]');
    const cur = drawer.querySelector('[data-cart-root]');
    if (next && cur) { markTierChange(cur, next); cur.replaceWith(next); }
    const label = doc.querySelector('[data-cart-count-label]');
    const curLabel = drawer.querySelector('[data-cart-count-label]');
    if (label && curLabel) curLabel.textContent = label.textContent;
  }
  if (page && sections[page.dataset.sectionId]) {
    const doc = new DOMParser().parseFromString(sections[page.dataset.sectionId], 'text/html');
    const next = doc.querySelector('[data-cart-page]');
    const cur = page.querySelector('[data-cart-page]');
    if (next && cur) {
      // The page title's arrival rise (title-in, yushe-v4.css) is for page loads, not every quantity tap.
      for (const h of next.querySelectorAll('h1')) h.style.animation = 'none';
      markTierChange(cur, next);
      cur.replaceWith(next);
    }
  }
}

function announceCount(itemCount) {
  document.dispatchEvent(
    new CustomEvent('cart:update', { bubbles: true, detail: { source: SRC, data: { itemCount, source: SRC } } })
  );
}

function setBusy(on) {
  for (const el of Object.values(roots())) el?.toggleAttribute('data-cart-busy', on);
}

/** Re-render from the current server state (no change). */
function refresh() {
  const ids = sectionIds();
  if (!ids.length) return Promise.resolve();
  return serial(async () => {
    // No Accept: application/json here: that would route to the cart JSON endpoint instead of the rendered sections.
    const res = await fetch(`${CART_URL}?sections=${ids.join(',')}`);
    if (res.ok) {
      const text = await res.text();
      try { apply(JSON.parse(text)); } catch {}
    }
  });
}

/** Change a line's quantity (0 removes it). */
function change(line, quantity, errorTarget) {
  const ids = sectionIds();
  return serial(async () => {
    setBusy(true);
    try {
      const res = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ line, quantity, sections: ids.join(','), sections_url: CART_URL }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.errors || data.status) {
        const msg = data.description || data.message || data.errors || 'Could not update your bag';
        if (errorTarget) {
          errorTarget.textContent = typeof msg === 'string' ? msg : String(msg);
          errorTarget.hidden = false;
        }
        return;
      }
      if (data.sections) apply(data.sections);
      announceCount(data.item_count);
    } finally {
      setBusy(false);
    }
  });
}

let noteTimer = 0;
function saveNote(value, status) {
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    serial(async () => {
      if (status) status.textContent = 'Saving…';
      const res = await fetch(UPDATE_URL, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ note: value }) });
      if (status) status.textContent = res.ok ? 'Saved' : 'Could not save the note';
    });
  }, 400);
}

/** Click handling shared by the drawer and the page (buttons are re-rendered, so delegate). */
function onClick(e) {
  const btn = e.target.closest('[data-cart-line]');
  if (!btn) return;
  e.preventDefault();
  const item = btn.closest('[data-cart-item]');
  const err = item?.querySelector('[data-cart-error]');
  if (err) err.hidden = true;
  change(Number(btn.dataset.cartLine), Number(btn.dataset.cartQty), err);
}
function onInput(e) {
  const ta = e.target.closest('[data-cart-note]');
  if (!ta) return;
  saveNote(ta.value, ta.parentElement.querySelector('[data-cart-note-status]'));
}

class YusheCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.isOpen = false;
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.addEventListener('click', onClick, { signal });
    for (const el of this.querySelectorAll('[data-cart="close"]')) el.addEventListener('click', () => this.close(), { signal });
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, { signal });
    // Swipe the bag back off to the right; the scrim lightens with the drag.
    attachDragDismiss(this.dialog, {
      panel: '.sheet__panel',
      fade: (p) => {
        const b = this.dialog.querySelector('.sheet__backdrop');
        if (!b) return;
        b.style.transition = p === null ? '' : 'none';
        b.style.opacity = p === null ? '' : String(1 - p);
      },
      dismiss: () => this.close(),
      signal,
    });
    // Links inside the drawer (view bag, checkout, product titles) close it on the way out.
    this.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (a && !a.hasAttribute('data-cart-line')) this.close(true);
    }, { signal });
    // The header's bag link: plain clicks open the drawer; modified clicks still go to /cart.
    for (const bag of document.querySelectorAll('[data-cart="open"]')) {
      bag.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        this.open();
      }, { signal });
    }
    // Adds from cards and product forms, and changes made on the bag page.
    document.addEventListener('cart:update', (e) => {
      const d = e.detail || {};
      const source = d.source || d.data?.source || d.sourceId || '';
      if (source === SRC) return;
      if (d.data?.didError) return;
      refresh().then(() => {
        if (document.querySelector('yushe-cart-page')) return;
        // Card quick-adds announce while their photo is still flying, so wait for it to land; the product page announces after landing.
        setTimeout(() => this.open(), source === 'yushe-card' ? 760 : 60);
      });
    }, { signal });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh().then(() => this.syncBubble()); }, { signal });
  }
  disconnectedCallback() { this.ac.abort(); this.unlockScroll(); }

  async syncBubble() {
    const cart = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
    if (cart) announceCount(cart.item_count);
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.opener = document.activeElement;
    this.lockScroll();
    this.dialog.showModal();
    requestAnimationFrame(() => requestAnimationFrame(() => { this.dialog.dataset.state = 'open'; }));
    this.querySelector('.cdrawer__close')?.focus({ preventScroll: true });
  }
  close(instant) {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dialog.dataset.state = 'closed';
    const done = () => {
      if (!this.isOpen && this.dialog.open) this.dialog.close();
      this.unlockScroll();
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    };
    if (instant || matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, CLOSE_MS);
  }
  lockScroll() {
    const root = document.documentElement;
    const gap = window.innerWidth - root.clientWidth;
    this.prev = { overflow: root.style.overflow, pad: root.style.paddingRight };
    root.style.overflow = 'hidden';
    if (gap) root.style.paddingRight = `${gap}px`;
  }
  unlockScroll() {
    if (!this.prev) return;
    const root = document.documentElement;
    root.style.overflow = this.prev.overflow;
    root.style.paddingRight = this.prev.pad;
    this.prev = null;
  }
}

class YusheCartPage extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.addEventListener('click', onClick, { signal });
    this.addEventListener('input', onInput, { signal });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); }, { signal });
  }
  disconnectedCallback() { this.ac.abort(); }
}

/** "You may also like" under the bag: Shopify's complementary recommendations, then related, else the fallback. */
class YusheCartRecs extends HTMLElement {
  async connectedCallback() {
    const { url, sectionId, productId } = this.dataset;
    if (!url || !productId) return;
    for (const intent of ['complementary', 'related']) {
      try {
        const res = await fetch(`${url}?section_id=${sectionId}&product_id=${productId}&limit=4&intent=${intent}`);
        if (!res.ok) continue;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = doc.querySelector('[data-recs-list]');
        if (list && list.children.length) {
          this.querySelector('[data-recs-list]')?.replaceWith(list);
          return;
        }
      } catch {}
    }
  }
}

/* ---------- Multi-buy: "add to unlock" picks and the "See all offers" sheet ---------- */
const money = (cents) => {
  const cur = window.Shopify?.currency?.active || 'USD';
  try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100); }
  catch { return `${(cents / 100).toFixed(2)}`; }
};
/** Rendered picks by request, so the bag's re-render after every change doesn't refetch the same list. */
const unlockCache = new Map();

/** <yushe-unlock>: products to add for the next tier, each priced with that tier's discount, one tap to add. */
class YusheUnlock extends HTMLElement {
  connectedCallback() {
    // The drawer is on every page, closed: fetch picks only once it is open, so page loads stay lean.
    const dlg = this.closest('dialog');
    if (!dlg || dlg.open) return this.load();
    this.mo = new MutationObserver(() => { if (dlg.open) { this.mo.disconnect(); this.load(); } });
    this.mo.observe(dlg, { attributes: true, attributeFilter: ['open'] });
  }
  disconnectedCallback() { this.mo?.disconnect(); }

  async load() {
    const { url, productId, exclude = '', pct = '0' } = this.dataset;
    if (!url || !productId) return;
    const key = `${productId}|${exclude}`;
    let html = unlockCache.get(key);
    if (!html) {
      try {
        const res = await fetch(`${url}?section_id=cart-unlock&product_id=${productId}&limit=10&intent=related`);
        if (!res.ok) return;
        html = await res.text();
        unlockCache.set(key, html);
      } catch { return; }
    }
    if (!this.isConnected) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const skip = new Set(exclude.split(',').filter(Boolean));
    const cards = [...doc.querySelectorAll('.ucard')].filter((c) => !skip.has(c.dataset.productId)).slice(0, 4);
    if (!cards.length) return this.remove();
    const off = Number(pct) || 0;
    for (const c of cards) {
      const price = Number(c.dataset.price) || 0;
      if (off && price) {
        c.querySelector('[data-unlock-price]').textContent = money(Math.round(price * (100 - off) / 100));
        const was = c.querySelector('[data-unlock-was]');
        was.textContent = money(price);
        was.hidden = false;
      }
    }
    const list = this.querySelector('[data-unlock-list]');
    list.replaceChildren(...cards);
    this.setAttribute('data-ready', '');
    list.addEventListener('click', (e) => {
      const b = e.target.closest('.ucard__size');
      if (b && !b.disabled) this.add(b);
    });
  }

  async add(btn) {
    if (this.busy) return;
    this.busy = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }] }) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        btn.closest('.ucard')?.setAttribute('data-error', d.description || 'Could not add');
        return;
      }
      // The drawer listens for this, re-renders (new tier, new picks) and stays open.
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'yushe-unlock', data: { source: 'yushe-unlock' } } }));
    } finally {
      this.busy = false;
      btn.removeAttribute('aria-busy');
    }
  }
}

/** "See all offers": opened from any [data-offers-open]; marks each tier from the live bag. */
function offersSheet() {
  const d = document.querySelector('dialog[data-offers]');
  if (!d || d.dataset.bound) return d;
  d.dataset.bound = '1';
  const close = () => {
    d.setAttribute('data-leaving', '');
    const done = () => { d.removeAttribute('data-leaving'); if (d.open) d.close(); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 220);
  };
  d.addEventListener('click', (e) => {
    if (e.target === d || e.target.closest('[data-offers-close]')) close();
  });
  d.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  d.addEventListener('close', () => {
    if (d.dataset.locked) { document.documentElement.style.overflow = ''; delete d.dataset.locked; }
  });
  // "Keep shopping" from inside the bag: close the bag too on the way.
  d.querySelector('[data-offers-shop]')?.addEventListener('click', () => document.querySelector('yushe-cart-drawer')?.close?.(true));
  return d;
}
async function openOffers() {
  const d = offersSheet();
  if (!d || d.open) return;
  d.showModal();
  if (document.documentElement.style.overflow !== 'hidden') { document.documentElement.style.overflow = 'hidden'; d.dataset.locked = '1'; }
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
  if (!cart) return;
  const count = cart.items.filter((i) => !i.gift_card).reduce((n, i) => n + i.quantity, 0);
  const tiers = [...d.querySelectorAll('.offers__tier')];
  let best = -1;
  tiers.forEach((t, i) => { if (count >= Number(t.dataset.qty)) best = i; });
  tiers.forEach((t, i) => {
    const need = Number(t.dataset.qty) - count;
    const state = t.querySelector('[data-offers-state]');
    t.toggleAttribute('data-on', i <= best);
    t.toggleAttribute('data-current', i === best);
    state.textContent = i === best ? 'Applied' : i < best ? '' : `Add ${need} more`;
  });
}
document.addEventListener('click', (e) => {
  if (e.target instanceof Element && e.target.closest('[data-offers-open]')) openOffers();
});

/** Product page line: after an add, say what one more of this piece unlocks for the bag as it now is. */
document.addEventListener('cart:update', async () => {
  const box = document.querySelector('[data-pdp-offer]');
  const line = box?.querySelector('[data-pdp-offer-bag]');
  if (!line) return;
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
  if (!cart) return;
  const inBag = cart.items.filter((i) => !i.gift_card).reduce((n, i) => n + i.quantity, 0);
  if (!inBag) return;
  const tiers = (box.dataset.tiers || '').split(',').map((t) => t.split(':').map(Number)).filter((t) => t.length === 2);
  const reached = (n) => tiers.filter(([q]) => n >= q).pop();
  const now = reached(inBag), next = reached(inBag + 1);
  line.textContent = next && (!now || next[1] > now[1])
    ? `You have ${inBag} in your bag: add one more for ${next[1]}% off everything.`
    : now ? `${now[1]}% off is applied to your bag.` : line.textContent;
});

if (!customElements.get('yushe-unlock')) customElements.define('yushe-unlock', YusheUnlock);
if (!customElements.get('yushe-cart-drawer')) customElements.define('yushe-cart-drawer', YusheCartDrawer);
if (!customElements.get('yushe-cart-page')) customElements.define('yushe-cart-page', YusheCartPage);
if (!customElements.get('yushe-cart-recs')) customElements.define('yushe-cart-recs', YusheCartRecs);
